import { memo, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  CellClickedEvent,
  ColDef,
  SideBarDef,
  GridApi,
} from "ag-grid-community";
import { Box, Dialog } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router";
import { getCurrencySymbol, getCurrencyType } from "../utils/currencyFormatter";

import { tableCellStyle, tableEntryStyle } from "../components/TableComponent";
import { useGetCompanyName, HStack } from "../components/utils";
import {
  useGetOnFilterState,
  usePostOnFilterState,
} from "../pages/dashboardPage/AgGridCacheQuery";
import { agConfigDashboardSideBar } from "../pages/dashboardPage/AgGridConfig";
import { useAuthStore } from "../store";
import { useAgGridTableState } from "../store/agGridTableStore";
import ExportMultidateSheetDetails from "./ExportMultidateSheetDetails";
import {
  PaidUpDateRender,
  PaidUpSharePriceRender,
  ScheduledDateRender,
  TotalPaidRender,
} from "./CurrentlyPaidAgComponent";
import { CurrentlyPaid } from "../types/Shareholder";
import { RedemptionSubmitDetails } from "../types/SecurityModel";
import { ShareholderNameRender } from "../pages/newQuickTransaction/QuickRoundConvertibleAgComponent";
import { useShareholderInstrumentStore } from "../store/shareholderInstrumentStore";
import { getShareholderValue } from "../constants/ShareholderConstants";
import { formatToReadableNumber } from "../utils/currencyRoboto";
import { formatDisplayDate } from "../utils/date";

function RedeemedAmountRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: data.data?.redeemedAmount,
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

function RedemptionDateRender(data: any) {
  return (
    <div className="whitespace-nowrap pt-2">
      <p className={tableEntryStyle}>
        {formatDisplayDate(data.data.redemptionDate)}
      </p>
    </div>
  );
}

function SecurityRedemptionsAgGridTable({
  shareholderRedemptions,
}: {
  shareholderRedemptions: RedemptionSubmitDetails[];
}) {
  const currencySymbol = getCurrencySymbol();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const [dialog, setDialog] = useState<{
    open: boolean;
    data?: string;
  }>({
    open: false,
  });

  const { refetch, data: filterState } = useGetOnFilterState(
    `${userId}`,
    companyId
  );

  const { mutate: postOnFilter } = usePostOnFilterState();

  const componentsRegistery = useMemo(
    () => ({
      shareholderName: memo(ShareholderNameRender),
      redeemedAmount: memo(RedeemedAmountRender),
      redemptionDate: memo(RedemptionDateRender),
    }),
    []
  );

  const navigate = useNavigate();

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 100,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  const instrumentShareholderInfoData = useShareholderInstrumentStore();

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Shareholder Name",
        field: "shareholderName",
        filter: "agMultiColumnFilter",
        getQuickFilterText: (params) => params.value.name,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Name",
              display: "subMenu",
              filter: "agTextColumnFilter",
              buttons: ["reset"],
              filterValueGetter: (params: any) => params.value.name,
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.getValue("shareholderName").name,
              },
            },
            {
              title: "Type",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const shareholderType = params.value.type;
                  return shareholderType;
                },
                valueFormatter: (params: any) => {
                  const shareholderType = getShareholderValue(
                    params.value.type
                  );
                  return shareholderType;
                },
              },
            },
          ],
        },
        pinned: "left",
        cellRenderer: "shareholderName",
        initialWidth: 300,
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipShareholderName",
      },
      {
        headerName: `Redeemed Amount (${currencySymbol})`,
        field: "redeemedAmount",
        filter: "agNumberColumnFilter",
        cellRenderer: "redeemedAmount",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Redemption Date",
        field: "redemptionDate",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0;
            }
            const dateParts = cellValue.split("-");
            const day = Number(dateParts[2]);
            const month = Number(dateParts[1]) - 1;
            const year = Number(dateParts[0]);
            const cellDate = new Date(year, month, day);
            if (cellDate < dateFromFilter) {
              return -1;
            } else if (cellDate > dateFromFilter) {
              return 1;
            }
            return 0;
          },
        },
        cellRenderer: "redemptionDate",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
    ],
    []
  );

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  const currencyType = getCurrencyType();

  const rowData = useMemo(
    () =>
      shareholderRedemptions?.map((redemption) => ({
        shareholderName: {
          name: instrumentShareholderInfoData?.shareholders?.find(
            (sh) => sh.id === redemption.shareholderId
          )?.name,
          type: instrumentShareholderInfoData?.shareholders?.find(
            (sh) => sh.id === redemption.shareholderId
          )?.type,
        },
        shareholderId: redemption.shareholderId,
        redeemedAmount: redemption.redeemedAmount,
        redemptionDate: redemption.redemptionDate,
        currencySymbol,
        currencyType,
      })),
    [shareholderRedemptions]
  );

  function setTableHeight() {
    return 4 * 60;
  }

  return (
    <>
      <HStack className="flex-col lg:flex-row justify-start lg:justify-between  py-2 lg:items-center items-start  bg-white rounded-md">
        <div className={`flex flex-col px-6 lg:justify-start `}>
          <p className="text-lg font-medium text-headerColor whitespace-nowrap">
            Redemptions
          </p>
        </div>
        <HStack className="w-full lg:w-auto lg:justify-end py-2 items-center gap-4 px-8"></HStack>
      </HStack>
      <Dialog open={dialog.open} maxWidth="md">
        <ExportMultidateSheetDetails
          reportName={dialog.data || ""}
          onPrimaryAction={() => setDialog({ open: false })}
          onSecondaryAction={() => setDialog({ open: false })}
        />
      </Dialog>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: setTableHeight(),
          }}
          className="w-full max-h-full overflow-x-auto ag-theme-material h-full "
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            components={componentsRegistery}
            alwaysShowHorizontalScroll={false}
            alwaysMultiSort
            animateRows={true}
            defaultColDef={defaultColDef}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={false}
            enableCharts={true}
            enableRangeSelection={true}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass={"border-t border-dashed"}
            gridOptions={gridOptions}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            }
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          ></AgGridReact>
        </Box>
      </HStack>
    </>
  );
}

export default SecurityRedemptionsAgGridTable;
